// Step 6 parity rules for the exit modal's "Move Account To" section
// (Step6ExitModelBean.buildScreen / moveAccountToUpdate). The BFF supplies the
// status and phase lists; these rules only decide whether the Phase dropdown
// shows and what phase is sent.
import type { NextStatusPhases } from "@credit-acceptance/step6-bff-client";

/* eslint-disable i18n-text/no-en -- BFF status and role identifiers, not UI copy */
export const REASSIGNMENT_STATUS = "Re-assignment Request";
const CONTRACT_RECEIVED_STATUS = "Contract Received";
const IMAGING_TEAM_ROLE = "IMAGING TEAM";

// Targets that hide Phase once the analyst changes the status: CR, RR, AP, PD, PS
// and Re-assignment. AP/PD/PS are matched on the BFF's status descriptions.
const PHASE_HIDDEN_TARGETS: ReadonlySet<string> = new Set([
  REASSIGNMENT_STATUS,
  CONTRACT_RECEIVED_STATUS,
  "Ready for Review",
  "Approval Not Worked",
  "Pending Deal",
  "Pending Signatures",
]);
/* eslint-enable i18n-text/no-en */

interface PhaseVisibilityInput {
  roleName?: string;
  currentStatus?: string;
  /** The analyst's selected target status. */
  targetStatus: string;
  /** True once the analyst has changed the target away from the initial selection. */
  targetChanged: boolean;
  /** Phases the BFF returned for the target. */
  phases?: readonly string[];
}

/**
 * Whether the Phase label and dropdown render. Hidden for an empty phase list, the
 * IMAGING TEAM role, and a deal whose current status is Contract Received (the
 * alternate-OK mode, which ordinary target changes do not leave). Status-driven
 * hiding (Re-assignment, CR, RR, AP, PD, PS) applies only after the target changes,
 * matching Step 6's initial-versus-change distinction.
 * @param input - Role, statuses, change flag and the returned phase list.
 * @param input.roleName - The analyst's role name.
 * @param input.currentStatus - The deal's current status description.
 * @param input.targetStatus - The selected target status.
 * @param input.targetChanged - Whether the analyst changed the target.
 * @param input.phases - Phases the BFF returned for the target.
 * @returns {boolean} True when Phase should render.
 */
export function isPhaseVisible({
  roleName,
  currentStatus,
  targetStatus,
  targetChanged,
  phases,
}: PhaseVisibilityInput): boolean {
  if (!phases?.length || roleName === IMAGING_TEAM_ROLE || currentStatus === CONTRACT_RECEIVED_STATUS) {
    return false;
  }
  return !(targetChanged && PHASE_HIDDEN_TARGETS.has(targetStatus));
}

/**
 * The phase to send on confirm: blank whenever Phase is hidden, never a stale
 * hidden selection. A blank phase is passed through as-is — no client-side
 * required-phase validation.
 * @param visible - Whether the Phase dropdown is showing.
 * @param selected - The currently selected phase.
 * @returns {string} The target phase to send.
 */
export function resolveTargetPhase(visible: boolean, selected: string): string {
  return visible ? selected : "";
}

/**
 * The phases the BFF paired with `status` in `nextStatuses`. A status with no
 * entry (or a response without `nextStatuses`) has no phases, so Phase hides.
 * @param nextStatuses - Each next status with its role-filtered phases.
 * @param status - The selected target status.
 * @returns {string[]} The target's phases, in BFF display order.
 */
export function phasesForStatus(nextStatuses: readonly NextStatusPhases[] | undefined, status: string): string[] {
  return nextStatuses?.find((entry) => entry.status === status)?.phases ?? [];
}

/**
 * Default phase for a target: its first phase, blank when it has none. The BFF
 * lists the deal's manual phase first for its current status, so that wins on
 * open. A previous target's selection is never carried over.
 * @param phases - Phases the BFF returned for the target.
 * @returns {string} The phase to select.
 */
export function defaultTargetPhase(phases: readonly string[]): string {
  return phases[0] ?? "";
}
